import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  Box,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Formik, Form } from 'formik';
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import axiosInstance from '../../api/axiosInstance';
import { endpoints } from '../../api/endPoints';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  timetableId: Yup.string().required('Session Time is required'),
  csrtId: Yup.string().nullable(),
  day: Yup.string().required('Day is required'),
  combination: Yup.string().nullable(),
});

const CreateClassTimetable = ({ open, onClose, onSuccess, classId }) => {
  const { getSubjectsWithTeachersByClassId, linkSessionTimeWithSubjectTeacher } = useSessionTimeRangeService();
  
  const [sessionTimes, setSessionTimes] = useState([]);
  const [subjectsTeachers, setSubjectsTeachers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (open) {
      loadData();
    } else {
      setSessionTimes([]);
      setSubjectsTeachers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId]);

  const loadData = async () => {
    setLoadingData(true);
    
    try {
      // Fetch all active session times
      try {
        const activeResponse = await axiosInstance.get(endpoints.sessionTimeRanges.getAllActive);
        
        let sessionTimesList = [];
        if (activeResponse.data && activeResponse.data.data) {
          sessionTimesList = Array.isArray(activeResponse.data.data) 
            ? activeResponse.data.data 
            : [];
        }
        
        // If getAllActive doesn't return data, use the paginated endpoint
        if (sessionTimesList.length === 0) {
          const paginatedUrl = `${endpoints.sessionTimeRanges.getAll}?page=0&size=1000&status=ACTIVE`;
          const paginatedResponse = await axiosInstance.get(paginatedUrl);
          
          if (paginatedResponse.data && paginatedResponse.data.data) {
            const paginatedData = paginatedResponse.data.data;
            
            if (paginatedData.data && Array.isArray(paginatedData.data)) {
              sessionTimesList = paginatedData.data.map((item) => ({
                id: item.id,
                sessionTime: item.sessionTime,
                timeDescription: item.timeDescription,
                status: item.status,
                createdAt: item.createdAt,
              }));
            }
          }
        } else {
          // Map the data to ensure consistent structure
          sessionTimesList = sessionTimesList.map((item) => ({
            id: item.id,
            sessionTime: item.sessionTime,
            timeDescription: item.timeDescription,
            status: item.status,
            createdAt: item.createdAt,
          }));
        }
        
        setSessionTimes(sessionTimesList);
      } catch (endpointError) {
        console.error('Error fetching session times:', endpointError);
        setSessionTimes([]);
      }

      // Fetch subjects and teachers for this class (only if classId is provided)
      if (classId) {
        try {
          const subjects = await getSubjectsWithTeachersByClassId(classId);
          const subjectsList = Array.isArray(subjects) ? subjects : [];
          setSubjectsTeachers(subjectsList);
        } catch (subjectError) {
          console.error('Error fetching subjects:', subjectError);
          setSubjectsTeachers([]);
        }
      } else {
        setSubjectsTeachers([]);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Error loading session times or subjects. Please try again.');
      }
      setSessionTimes([]);
      setSubjectsTeachers([]);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsSubmitting(true);
    try {
      // Convert special time options and empty string to null for break/lunch/prayer times
      // Only actual csrtId values (UUIDs) should be passed, others become null
      let csrtId = null;
      if (values.csrtId && 
          values.csrtId !== '' && 
          values.csrtId !== 'BREAK_TIME' && 
          values.csrtId !== 'LUNCH_TIME' && 
          values.csrtId !== 'PRAYER_TIME') {
        csrtId = values.csrtId;
      }
      
      // Combination is optional - use it if provided
      const combination = values.combination || null;
      
      await linkSessionTimeWithSubjectTeacher(values.timetableId, csrtId, values.day, combination);
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Link session time failed:", error.response?.data || error.message);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const combinations = ["ALL", "SCIENCE", "PCB", "PCM", "HGE", "EGM", "HGL", "ART", "BUSINESS"];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>CREATE CLASS TIMETABLE</DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {loadingData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={{
              timetableId: '',
              csrtId: '', // Start blank - user must choose
              day: '',
              combination: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting: formikSubmitting,
              setFieldValue,
            }) => {
              // Prepare options for Subject-Teacher Autocomplete
              const subjectTeacherOptions = [
                { id: 'BREAK_TIME', label: 'Break Time (No Subject-Teacher)', value: 'BREAK_TIME' },
                ...subjectsTeachers.map((item) => ({
                  id: item.csrtId,
                  label: `${item.subjectName || 'N/A'} - ${item.teacherName || 'No Teacher'}`,
                  value: item.csrtId,
                })),
              ];

              // Find selected session time object
              const selectedSessionTime = sessionTimes.find((time) => time.id === values.timetableId) || null;

              // Find selected subject-teacher object
              const selectedSubjectTeacher = subjectTeacherOptions.find(
                (option) => option.value === values.csrtId
              ) || null;

              // Find selected day
              const selectedDay = days.find((day) => day === values.day) || null;

              // Determine if combination field should be enabled (only when a teacher-subject is selected)
              const isCombinationEnabled = values.csrtId && 
                values.csrtId !== '' && 
                values.csrtId !== 'BREAK_TIME' && 
                values.csrtId !== 'LUNCH_TIME' && 
                values.csrtId !== 'PRAYER_TIME';

              return (
              <Form>
                {isSubmitting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={sessionTimes}
                      getOptionLabel={(option) => 
                        `${option.sessionTime || 'N/A'}${option.timeDescription ? ` - ${option.timeDescription}` : ''}`
                      }
                      value={selectedSessionTime}
                      onChange={(event, newValue) => {
                        setFieldValue('timetableId', newValue ? newValue.id : '', true);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting || formikSubmitting || loadingData}
                      loading={loadingData}
                      noOptionsText={
                        !loadingData && sessionTimes.length === 0
                          ? "No session times available. Please create session times first."
                          : "No options found"
                      }
                      filterOptions={(options, { inputValue }) => {
                        return options.filter((option) => {
                          const searchText = inputValue?.toLowerCase() || '';
                          const sessionTime = (option.sessionTime || '').toLowerCase();
                          const timeDescription = (option.timeDescription || '').toLowerCase();
                          return sessionTime.includes(searchText) || timeDescription.includes(searchText);
                        });
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Session Time *"
                          variant="filled"
                          error={touched.timetableId && Boolean(errors.timetableId)}
                          helperText={
                            touched.timetableId && errors.timetableId
                              ? errors.timetableId
                              : sessionTimes.length === 0 && !loadingData
                              ? "No session times found. Create session times from Session Time Ranges page."
                              : null
                          }
                          placeholder="Search and select a session time..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={subjectTeacherOptions}
                      getOptionLabel={(option) => option.label}
                      value={selectedSubjectTeacher}
                      onChange={(event, newValue) => {
                        setFieldValue('csrtId', newValue ? newValue.value : '', true);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting || formikSubmitting || loadingData}
                      noOptionsText={
                        subjectsTeachers.length === 0
                          ? "No subjects-teachers available for this class."
                          : "No options found"
                      }
                      filterOptions={(options, { inputValue }) => {
                        return options.filter((option) => {
                          const searchText = inputValue?.toLowerCase() || '';
                          const label = (option.label || '').toLowerCase();
                          return label.includes(searchText);
                        });
                      }}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Subject - Teacher"
                          variant="filled"
                          error={touched.csrtId && Boolean(errors.csrtId)}
                          helperText={touched.csrtId && errors.csrtId}
                          placeholder="Search and select subject-teacher or special time..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      options={days}
                      getOptionLabel={(option) => option}
                      value={selectedDay}
                      onChange={(event, newValue) => {
                        setFieldValue('day', newValue || '', true);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting || formikSubmitting}
                      noOptionsText="No options found"
                      filterOptions={(options, { inputValue }) => {
                        return options.filter((option) => {
                          const searchText = inputValue?.toLowerCase() || '';
                          const day = option.toLowerCase();
                          return day.includes(searchText);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Day *"
                          variant="filled"
                          error={touched.day && Boolean(errors.day)}
                          helperText={touched.day && errors.day}
                          placeholder="Search and select a day..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Combination</InputLabel>
                      <Select
                        name="combination"
                        value={values.combination || ''}
                        onChange={(event) => {
                          setFieldValue('combination', event.target.value, true);
                        }}
                        onBlur={handleBlur}
                        error={touched.combination && Boolean(errors.combination)}
                        disabled={isSubmitting || formikSubmitting || !isCombinationEnabled}
                      >
                        <MenuItem value="">
                          <em>Select a combination (optional)</em>
                        </MenuItem>
                        {combinations.map((combo) => (
                          <MenuItem key={combo} value={combo}>
                            {combo}
                          </MenuItem>
                        ))}
                      </Select>
                        {touched.combination && errors.combination && (
                          <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                            {errors.combination}
                          </Box>
                        )}
                        {!isCombinationEnabled && (
                          <Box sx={{ color: 'info.main', fontSize: '0.75rem', mt: 0.5 }}>
                            Combination is available when assigning a teacher-subject
                          </Box>
                        )}
                    </FormControl>
                  </Grid>
                </Grid>

                <DialogActions sx={{ mt: 2 }}>
                  <Button onClick={onClose} disabled={isSubmitting || formikSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || formikSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  >
                    {isSubmitting ? 'Linking...' : 'Link'}
                  </Button>
                </DialogActions>
              </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateClassTimetable;

